/* eslint-env node */
/**
 * mooFlowBuilder — logique PURE de construction de flow (sans Vue / DOM / axios).
 *
 * Partagé entre :
 *   - le dashboard (PlaygroundView.vue → handleAiCommands), qui exécute les
 *     actions renvoyées par Moo pour construire réellement les blocs ;
 *   - les harnais de test Node, qui rejouent ces mêmes actions headless.
 *
 * But : ZÉRO DÉRIVE. Tout ce qui touche aux templates d'éléments, à la génération
 * d'ids (bloc / élément), au chaînage (id_element / id_previous) et à la résolution
 * des références (blockname → idblock, nom de variable → slug) vit ICI, et le front
 * comme les tests consomment exactement le même code.
 *
 * Écrit en CommonJS (module.exports) pour être importable à la fois par webpack
 * (vue-cli, interop des imports nommés) et par Node (require natif).
 */

// Templates par type d'élément (défauts appliqués avant fusion avec les properties de Moo).
const elementTemplate = {
  text: {
    type: 'text',
    id_element: null,
    id_previous: null,
    message: '',
    preview_url: true
  },
  text_button: {
    type: 'text_button',
    id_element: null,
    id_previous: null,
    message: 'Ecris ton texte ici',
    buttons: [
      { id: 'idbutton1', title: 'Placeholder' },
      { id: 'idbutton2', title: 'Placeholder' }
    ]
  },
  list: {
    type: 'list',
    id_element: null,
    id_previous: null,
    interactive: {
      type: 'list',
      header: { type: 'text', text: 'HEADER_TEXT' },
      body: { text: 'BODY_TEXT' },
      footer: { text: 'FOOTER_TEXT' },
      action: {
        button: 'BUTTON_TEXT',
        sections: [
          {
            title: 'SECTION_1_TITLE',
            rows: [
              { id: 'SECTION_1_ROW_1_ID', title: 'SECTION_1_ROW_1_TITLE', description: 'SECTION_1_ROW_1_DESCRIPTION' },
              { id: 'SECTION_1_ROW_2_ID', title: 'SECTION_1_ROW_2_TITLE', description: 'SECTION_1_ROW_2_DESCRIPTION' }
            ]
          },
          {
            title: 'SECTION_2_TITLE',
            rows: [
              { id: 'SECTION_2_ROW_1_ID', title: 'SECTION_2_ROW_1_TITLE', description: 'SECTION_2_ROW_1_DESCRIPTION' },
              { id: 'SECTION_2_ROW_2_ID', title: 'SECTION_2_ROW_2_TITLE', description: 'SECTION_2_ROW_2_DESCRIPTION' }
            ]
          }
        ]
      }
    }
  },
  image: { type: 'image', id_element: null, id_previous: null, link: '' },
  ask_payment: {
    type: 'ask_payment',
    id_element: null,
    id_previous: null,
    provider: 'wave',
    amount: 100,
    client_reference: '',
    success_block: '',
    error_block: ''
  },
  document: { type: 'document', id_element: null, id_previous: null, link: '' },
  audio: { type: 'audio', id_element: null, id_previous: null, link: '' },
  audio_wolof: { type: 'audio_wolof', id_element: null, id_previous: null, text: '', link: null, wolof: '', texte_audio: '', deja_wolof: false, langue: 'wolof', voix: 'Kore', attendre: true, delai: 5, conditions: [], logic_gate: 'AND', historique: [] },
  video: { type: 'video', id_element: null, id_previous: null, link: '' },
  delay: { type: 'delay', id_element: null, id_previous: null, timmer: '' },
  action: { type: 'action', action_id: '' },
  request: {
    type: 'request',
    id_element: null,
    id_previous: null,
    method: 'POST',
    url: 'https://api.peelo.io/send/datas',
    datas: ['variablename1', 'variablename2', 'variablename3']
  },
  answer: {
    type: 'answer',
    id_element: null,
    id_previous: null,
    options: {
      answer_type: ['text'],
      variable: { id: 'departureter' },
      default_reply: 'Désolé je ne comprends pas, ...'
    }
  },
  redirect: {
    type: 'redirection',
    id_element: null,
    id_previous: null,
    redirection_block: 'block name'
  },
  notification: {
    type: 'notification',
    to: '2217XXXXXXXX',
    id_element: null,
    id_previous: null,
    notification: { type: 'text', message: '', preview_url: true }
  },
  variable_insert: {
    type: 'variable_insert',
    id_element: null,
    id_previous: null,
    variable: { id: '', value: '' }
  },
  location_request_message: {
    type: 'location_request_message',
    id_element: null,
    id_previous: null,
    message: 'Vous devez cliquer sur le bouton ci-dessous pour envoyer votre localisation'
  },
  button_link: {
    type: 'button_link',
    id_element: null,
    id_previous: null,
    image_header: '',
    body_message: 'Tap the button below to see available dates.',
    action: {
      name: 'cta_url',
      parameters: { display_text: 'See Dates', url: 'https://example.com' }
    },
    footer_message: 'Dates subject to change.'
  },
  template_message: {
    type: 'template_message',
    id_element: null,
    id_previous: null,
    sender: '',
    to: '',
    templateName: '',
    parameters: {}
  },
  agent_handoff: {
    type: 'agent_handoff',
    id_element: null,
    id_previous: null,
    talkToAi: false,
    label: 'Stop AI'
  },
  condition: {
    type: 'condition',
    id_element: null,
    id_previous: null,
    conditions: [{ variable: '', operator: '==', value: '' }],
    logic_gate: 'AND',
    true_block: ''
  }
};

function clone(o) {
  return JSON.parse(JSON.stringify(o));
}

// Chaîne aléatoire alphanumérique (a-z0-9).
function generateRandomId(length = 8) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Slug canonique d'un nom de variable (identique à la création de variable côté front).
function slugify(name) {
  if (!name) return '';
  return String(name)
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // supprime les accents
    .toLowerCase()
    .replace(/\s+/g, '_')        // espaces → _
    .replace(/[^a-z0-9_]/g, ''); // garde lettres/chiffres/_
}

// Génère un idblock à partir d'un nom (slug tireté ≤ 13 + '-' + 6 chars, total ≤ 20).
function generateBlockId(name) {
  if (!name) return '';
  let baseId = String(name)
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (baseId.length > 13) baseId = baseId.substring(0, 13);
  const randomString = generateRandomId(6);
  let idblock = `${baseId}-${randomString}`;
  if (idblock.length > 20) {
    baseId = baseId.substring(0, baseId.length - (idblock.length - 20));
    idblock = `${baseId}-${randomString}`;
  }
  return idblock;
}

// Clé "lâche" pour comparer des noms/ids de bloc malgré casse, accents et séparateurs
// (« Menu Principal » / « menu-principal » / « menu_principal » → "menu-principal").
function looseKey(s) {
  return String(s || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Résout une référence de bloc vers son idblock réel.
// 1) égalité stricte (idblock / blockname / displayName) ;
// 2) sinon égalité "lâche" sur le nom (corrige les slugs devinés par Moo,
//    ex. bouton id="infos" → bloc "Infos" → idblock "infos-xxxxx").
// Si rien ne matche, renvoie la valeur d'entrée inchangée (ce n'était pas une réf de bloc).
function resolveBlockId(nameOrId, blocks = []) {
  if (!nameOrId) return nameOrId;
  let target = blocks.find(b =>
    b.idblock === nameOrId || b.blockname === nameOrId || b.displayName === nameOrId);
  if (target) return target.idblock;
  const key = looseKey(nameOrId);
  if (key) {
    target = blocks.find(b => looseKey(b.blockname) === key || looseKey(b.displayName) === key);
    if (target) return target.idblock;
  }
  return nameOrId;
}

// Résout un nom de variable vers son slug (en réutilisant une variable existante si trouvée).
function resolveVarId(name, variables = []) {
  const slug = slugify(name);
  const found = (variables || []).find(v => v.slug === name || v.id === name || v.slug === slug);
  return found ? (found.slug || found.id) : slug;
}

/**
 * Construit un élément de flow à partir d'une action insert_element de Moo :
 * template du type + properties fusionnées + résolution des références.
 * Renvoie null si le type est inconnu (l'appelant décide quoi faire).
 */
function buildElement({ elementType, properties, blocks = [], variables = [] }) {
  const template = elementTemplate[elementType];
  if (!template) return null;

  const props = clone(properties || {});

  // blockname → idblock pour les redirections et conditions
  if (elementType === 'redirect' && props.redirection_block) {
    props.redirection_block = resolveBlockId(props.redirection_block, blocks);
  }
  if (elementType === 'condition' && props.true_block) {
    props.true_block = resolveBlockId(props.true_block, blocks);
  }
  // nom de variable → slug pour answer et variable_insert
  if (elementType === 'answer' && props.options && props.options.variable && props.options.variable.id) {
    props.options.variable.id = resolveVarId(props.options.variable.id, variables);
  }
  if (elementType === 'variable_insert' && props.variable && props.variable.id) {
    props.variable.id = resolveVarId(props.variable.id, variables);
  }
  // blockname → idblock pour les boutons et les lignes de liste (cibles de redirection)
  if (elementType === 'text_button' && Array.isArray(props.buttons)) {
    props.buttons.forEach(btn => { if (btn && btn.id) btn.id = resolveBlockId(btn.id, blocks); });
  }
  if (elementType === 'list' && props.interactive && props.interactive.action && props.interactive.action.sections) {
    props.interactive.action.sections.forEach(section => {
      if (Array.isArray(section.rows)) {
        section.rows.forEach(row => { if (row && row.id) row.id = resolveBlockId(row.id, blocks); });
      }
    });
  }

  const el = Object.assign(clone(template), props);

  // `Object.assign` est SHALLOW : pour une `list`, le `interactive` fourni par Moo REMPLACE
  // entièrement celui du template → si Moo omet `footer` (ou header/body/action), ces champs
  // disparaissent. Or l'éditeur (ListElement.vue) lit `interactive.footer.text` → crash au rendu
  // → le bloc paraît VIDE. On garantit donc une structure `interactive` complète.
  if (elementType === 'list') {
    const it = (el.interactive && typeof el.interactive === 'object') ? el.interactive : {};
    it.type = it.type || 'list';
    if (!it.header || typeof it.header !== 'object') it.header = { type: 'text', text: '' };
    if (it.header.text == null) it.header.text = '';
    if (!it.body || typeof it.body !== 'object') it.body = { text: '' };
    if (it.body.text == null) it.body.text = '';
    if (!it.footer || typeof it.footer !== 'object') it.footer = { text: '' };
    if (it.footer.text == null) it.footer.text = '';
    if (!it.action || typeof it.action !== 'object') it.action = { button: 'Choisir', sections: [] };
    if (it.action.button == null) it.action.button = 'Choisir';
    if (!Array.isArray(it.action.sections)) it.action.sections = [];
    el.interactive = it;
  }

  return el;
}

/**
 * Pose id_element (s'il manque) et id_previous sur un élément en position `index`
 * d'un `flow`. id_element = `${idbot}_${blockId}_${type}_${random}`.
 */
function chainElement(element, index, flow, idbot, blockId) {
  if (!element) return element;
  if (!element.id_element) {
    const elementType = element.type || 'unknown';
    element.id_element = `${idbot}_${blockId}_${elementType}_${generateRandomId()}`;
  }
  element.id_previous = index === 0 ? null : flow[index - 1].id_element;
  return element;
}

// Applique chainElement sur tout un flow.
function chainFlow(flow, idbot, blockId) {
  return (flow || []).map((el, i) => chainElement(el, i, flow, idbot, blockId));
}

// Re-résout TOUTES les cibles de navigation d'un élément (boutons, redirection,
// lignes de liste, condition) contre l'ensemble complet des blocs. À appeler une
// fois tous les blocs créés : corrige les références "en avant" (cible créée après
// l'élément qui la pointe) et les slugs devinés par Moo. Renvoie true si modifié.
function relinkElement(el, blocks = []) {
  if (!el) return false;
  let dirty = false;
  const fix = (cur) => {
    const r = resolveBlockId(cur, blocks);
    return (r !== cur) ? r : null;
  };
  if (el.type === 'redirection' && el.redirection_block) {
    const r = fix(el.redirection_block); if (r) { el.redirection_block = r; dirty = true; }
  }
  if (el.type === 'condition' && el.true_block) {
    const r = fix(el.true_block); if (r) { el.true_block = r; dirty = true; }
  }
  if (el.type === 'text_button' && Array.isArray(el.buttons)) {
    el.buttons.forEach(btn => { if (btn && btn.id) { const r = fix(btn.id); if (r) { btn.id = r; dirty = true; } } });
  }
  if (el.type === 'list' && el.interactive && el.interactive.action && el.interactive.action.sections) {
    el.interactive.action.sections.forEach(section => {
      (section.rows || []).forEach(row => { if (row && row.id) { const r = fix(row.id); if (r) { row.id = r; dirty = true; } } });
    });
  }
  return dirty;
}

// Valeur "propre" dérivée d'un titre de bouton pour servir de valeur de payload/condition :
// retire emoji/accents, minuscule, sépare par "-" et coupe les bords. "✅ Oui" → "oui".
function cleanChoiceValue(title) {
  return looseKey(title);
}

// Ensemble des variables réellement testées par une `condition` (dans tous les blocs).
// Sert à n'ajouter un payload QUE quand une valeur propre est nécessaire à une comparaison.
function variablesUsedInConditions(blocks = []) {
  const set = new Set();
  for (const b of blocks) {
    for (const el of (b.flow || [])) {
      if (el && el.type === 'condition' && Array.isArray(el.conditions)) {
        el.conditions.forEach(c => { if (c && c.variable) set.add(c.variable); });
      }
    }
  }
  return set;
}

/**
 * Garantit que les boutons de choix portent un `payload` correct — UNIQUEMENT quand c'est utile.
 * Condition d'injection : un `text_button` immédiatement suivi d'un `answer(variable V)`, ET V est
 * réellement **testée par une `condition`** quelque part (sinon le payload n'est pas nécessaire :
 * boutons de navigation, réponse juste archivée/affichée, etc. → on ne touche à rien).
 * Quand utile, chaque bouton SANS payload reçoit `payload: "{{V}}=valeur_propre"` (valeur dérivée
 * du titre, sans emoji/accent). Ne touche JAMAIS un payload déjà présent (Moo reste maître).
 * Équivalent automatique de l'éditeur TextButtonElement.vue. Renvoie les blocs modifiés.
 */
function ensureButtonPayloads(blocks = []) {
  const condVars = variablesUsedInConditions(blocks);
  if (condVars.size === 0) return []; // aucune condition → aucun payload nécessaire
  const changed = [];
  for (const b of blocks) {
    const flow = b.flow || [];
    let dirty = false;
    for (let i = 0; i < flow.length; i++) {
      const el = flow[i];
      if (!el || el.type !== 'text_button' || !Array.isArray(el.buttons)) continue;
      // L'élément suivant doit être un answer qui capte le clic dans une variable...
      const next = flow[i + 1];
      const varId = next && next.type === 'answer'
        && next.options && next.options.variable && next.options.variable.id;
      if (!varId) continue;
      // ...et cette variable doit être testée par une condition (sinon payload inutile).
      if (!condVars.has(varId)) continue;
      el.buttons.forEach(btn => {
        if (!btn) return;
        if (btn.payload && String(btn.payload).trim()) return; // ne jamais écraser
        const value = cleanChoiceValue(btn.title);
        if (!value) return;
        btn.payload = `{{${varId}}}=${value}`;
        dirty = true;
      });
    }
    if (dirty) changed.push(b);
  }
  return changed;
}

// Tronque une chaîne à n caractères (limites WhatsApp sur titres/labels).
function truncate(s, n) { s = String(s == null ? '' : s); return s.length > n ? s.slice(0, n) : s; }

// Limites WhatsApp : un text_button affiche AU MAXIMUM 3 boutons. Au-delà, le message
// n'apparaît NI dans agenttalk NI sur WhatsApp (l'API rejette). Une liste interactive
// accepte jusqu'à 10 lignes.
const WA_MAX_BUTTONS = 3;
const WA_MAX_LIST_ROWS = 10;

// Convertit un text_button (boutons) en élément `list` fonctionnellement équivalent :
// chaque bouton -> une ligne (row.id = cible de redirection, comme un bouton) ; si un
// `answer(variable V)` suit, la liste porte options.variable={id:V} (le runtime stocke
// list_reply dans V, exactement comme la prod). Conserve le chaînage (id_element/id_previous).
function buttonsToList(el, nextEl) {
  const src = (el.buttons || []).slice(0, WA_MAX_LIST_ROWS);
  const rows = src.map((b, i) => ({
    id: (b && b.id) || `row_${i + 1}`,
    title: truncate((b && b.title) || `Option ${i + 1}`, 24),
    description: truncate((b && b.description) || '', 72)
  }));
  const list = {
    type: 'list',
    id_element: el.id_element || null,   // conserve le chaînage (les id_previous suivants pointent ici)
    id_previous: el.id_previous || null,
    interactive: {
      type: 'list',
      body: { text: truncate(el.message || 'Choisis une option :', 4096) },
      action: { button: 'Choisir', sections: [{ title: truncate('Options', 24), rows }] }
    }
  };
  const varId = nextEl && nextEl.type === 'answer' && nextEl.options && nextEl.options.variable && nextEl.options.variable.id;
  if (varId) list.options = { variable: { id: varId } };
  return list;
}

/**
 * Garantit la règle WhatsApp « max 3 boutons » : tout `text_button` de plus de 3 boutons
 * est converti en `list` (sinon il ne s'affiche pas du tout). À appeler AVANT relinkBlocks
 * (la liste produite est ensuite re-liée comme n'importe quelle liste) et ensureButtonPayloads
 * (qui ne touche que les text_button restants ≤ 3). Renvoie les blocs modifiés.
 */
function enforceButtonLimit(blocks = []) {
  const changed = [];
  for (const b of blocks) {
    const flow = b.flow || [];
    let dirty = false;
    for (let i = 0; i < flow.length; i++) {
      const el = flow[i];
      if (el && el.type === 'text_button' && Array.isArray(el.buttons) && el.buttons.length > WA_MAX_BUTTONS) {
        flow[i] = buttonsToList(el, flow[i + 1]);
        dirty = true;
      }
    }
    if (dirty) changed.push(b);
  }
  return changed;
}

/**
 * Garantit la CAPTURE d'une sélection de `list` : si une `list` est immédiatement suivie d'un
 * `answer(variable V)` mais que la liste n'a pas de `options.variable`, on recopie V sur la liste.
 * Pourquoi : au runtime la valeur d'une liste est enregistrée à l'intake VIA `options.variable`
 * de la liste — pas par l'`answer`. Si les `row.id` pointent vers un bloc (navigation), le saut
 * court-circuite l'`answer` et, sans `options.variable`, la valeur est PERDUE (bug profil FAMA :
 * âge/région/situation non stockés). Cette passe ne touche jamais une liste qui a déjà
 * `options.variable` (Moo reste maître). Renvoie les blocs modifiés.
 */
function ensureListCaptureVariable(blocks = []) {
  const changed = [];
  for (const b of blocks) {
    const flow = b.flow || [];
    let dirty = false;
    for (let i = 0; i < flow.length; i++) {
      const el = flow[i];
      if (!el || el.type !== 'list') continue;
      if (el.options && el.options.variable && el.options.variable.id) continue; // déjà capturée
      const next = flow[i + 1];
      const varId = next && next.type === 'answer'
        && next.options && next.options.variable && next.options.variable.id;
      if (!varId) continue;
      el.options = Object.assign({}, el.options, { variable: { id: varId } });
      dirty = true;
    }
    if (dirty) changed.push(b);
  }
  return changed;
}

// Types de blocs système (jamais des cibles d'accueil).
const SYSTEM_BLOCK_TYPES = ['starting_point', 'default_block', 'commerce_point'];

// Trouve le bloc système d'entrée (Welcome Flow / starting_point) dans la liste.
function findWelcomeBlock(blocks = []) {
  return blocks.find(b => b && (b.type === 'starting_point'
    || /welcome/i.test(b.displayName || '') || /welcome/i.test(b.blockname || '') || /welcomeblock/i.test(b.idblock || ''))) || null;
}

// Détermine le bloc d'accueil (point d'entrée du parcours) : un bloc standard nommé
// accueil/bienvenue/welcome/start, sinon le PREMIER bloc standard créé.
function findEntryBlock(blocks = []) {
  const std = blocks.filter(b => b && b.idblock && !SYSTEM_BLOCK_TYPES.includes(b.type));
  const named = std.find(b => /accueil|bienvenue|welcome|d[ée]marr|^start|onboard/i.test(b.blockname || b.displayName || ''));
  return named || std[0] || null;
}

/**
 * Garantit que la conversation DÉMARRE : le bloc système `Welcome Flow` (starting_point) est
 * le point d'entrée réel (déclenché au 1er message / `<begin>`). S'il ne contient pas déjà de
 * `redirection`, on lui en ajoute une vers le bloc d'accueil (1er bloc standard). Ainsi le bot
 * démarre toujours, même si Moo a construit l'accueil dans un bloc séparé sans relier le Welcome
 * Flow. Approche choisie : Accueil = bloc standard ; Welcome Flow = simple redirection vers lui.
 * (Rediriger DEPUIS le Welcome Flow est autorisé — la Règle 6 n'interdit que rediriger VERS lui.)
 * Renvoie les blocs modifiés (ici le Welcome Flow, à re-sauvegarder via son type starting_point).
 */
function ensureWelcomeRedirect(blocks = []) {
  const welcome = findWelcomeBlock(blocks);
  if (!welcome) return [];
  const flow = welcome.flow || (welcome.flow = []);
  if (flow.some(el => el && el.type === 'redirection' && el.redirection_block)) return []; // déjà relié
  const entry = findEntryBlock(blocks);
  if (!entry) return [];
  const redirect = buildElement({ elementType: 'redirect', properties: { redirection_block: entry.idblock }, blocks });
  if (!redirect) return [];
  flow.push(redirect);
  return [welcome];
}

// Une cible de navigation (id de bouton, row.id, redirection_block, true_block) est
// "résolue" si elle pointe vers un bloc réel de l'ensemble. Sert à détecter les cibles mortes.
function targetResolves(ref, blocks = []) {
  if (!ref) return false;
  const resolved = resolveBlockId(ref, blocks);
  return blocks.some(b => b && b.idblock === resolved);
}

/**
 * DÉTECTION (NON mutante) des « interactions mortes » : un bloc qui PRÉSENTE un choix
 * interactif (`text_button`/`list`) dont AUCUNE option ne pointe vers un vrai bloc, ET qui
 * n'a ni `condition` ni `redirect` valides pour router autrement. Au runtime, cliquer mène
 * alors nulle part → le parcours s'arrête mort (cf. bug Consentement : boutons `btn_oui_consent`
 * sans cible). On NE corrige PAS automatiquement : la bonne destination n'est pas déductible
 * sans risque de boucle (cf. Règle 3) ; on se contente de SIGNALER pour les tests / l'UI.
 * Un bloc purement terminal (que des `text`) n'est jamais signalé (dead end légitime).
 * Renvoie [{ idblock, blockname, element, deadTargets }].
 */
function findDeadInteractions(blocks = []) {
  const issues = [];
  for (const b of blocks) {
    const flow = b.flow || [];
    const hasRoute = flow.some(el =>
      (el && el.type === 'redirection' && targetResolves(el.redirection_block, blocks)) ||
      (el && el.type === 'condition' && targetResolves(el.true_block, blocks)));
    if (hasRoute) continue; // une condition/redirect valide assure déjà une sortie
    for (const el of flow) {
      if (!el) continue;
      let targets = [];
      if (el.type === 'text_button' && Array.isArray(el.buttons)) {
        targets = el.buttons.map(btn => btn && btn.id).filter(Boolean);
      } else if (el.type === 'list' && el.interactive && el.interactive.action && Array.isArray(el.interactive.action.sections)) {
        targets = el.interactive.action.sections
          .flatMap(s => (s.rows || []).map(r => r && r.id)).filter(Boolean);
      } else {
        continue;
      }
      if (!targets.length) continue;
      const dead = targets.filter(t => !targetResolves(t, blocks));
      // Signalé seulement si TOUTES les options sont mortes (et pas de condition/redirect de secours).
      if (dead.length === targets.length) {
        issues.push({ idblock: b.idblock, blockname: b.blockname, element: el.type, deadTargets: dead });
      }
    }
  }
  return issues;
}

// Re-lie tous les blocs entre eux. Renvoie la liste des blocs modifiés (à re-sauvegarder).
function relinkBlocks(blocks = []) {
  const changed = [];
  for (const b of blocks) {
    let dirty = false;
    for (const el of (b.flow || [])) {
      if (relinkElement(el, blocks)) dirty = true;
    }
    if (dirty) changed.push(b);
  }
  return changed;
}

// ---------------------------------------------------------------------------
// Bloc « Récap variables » (test) : un bloc isolé (hors parcours) qui affiche
// toutes les variables du bot et leurs valeurs courantes via {{slug}}. Sert à
// vérifier d'un coup, en activant le bloc, que la capture a bien pris.
// ---------------------------------------------------------------------------
const RECAP_BLOCK_NAME = '🔍 Récap variables (test)';

// Construit le message texte du bloc récap à partir des variables déclarées.
function buildVariableRecapMessage(variables = []) {
  const vars = (variables || []).filter(v => v && (v.slug || v.id) && v.type !== 'global');
  const lines = vars.length
    ? vars.map(v => { const s = v.slug || v.id; return `• ${s} : {{${s}}}`; }).join('\n')
    : '(aucune variable déclarée pour l\'instant)';
  return `🔍 RÉCAP VARIABLES (bloc de test)\n\n${lines}\n\n— Bloc de vérification : active-le pour voir les valeurs capturées. L'utilisateur final ne le voit pas.`;
}

module.exports = {
  elementTemplate,
  RECAP_BLOCK_NAME,
  buildVariableRecapMessage,
  generateRandomId,
  slugify,
  looseKey,
  generateBlockId,
  resolveBlockId,
  resolveVarId,
  buildElement,
  chainElement,
  chainFlow,
  relinkElement,
  relinkBlocks,
  cleanChoiceValue,
  ensureButtonPayloads,
  enforceButtonLimit,
  ensureListCaptureVariable,
  findWelcomeBlock,
  findEntryBlock,
  ensureWelcomeRedirect,
  targetResolves,
  findDeadInteractions
};
